import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Customer } from '@/lib/account-api';
import { fetchMe, signOut as apiSignOut } from '@/lib/account-api';
import { setAffiliateCustomerId } from '@/lib/affiliate';
import { setProductEventsCustomerId } from '@/lib/product-events';

interface AuthContextValue {
  customer: Customer | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
  setCustomer: (customer: Customer | null) => void;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  customer: null,
  isLoading: false,
  refresh: async () => {},
  setCustomer: () => {},
  logout: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

// Hint flag stored in localStorage so AuthContext can skip the eager `/me`
// probe for visitors who have never signed in. The auth cookie itself is
// HttpOnly so JS can't inspect it directly. The flag is purely an
// optimisation: if it disagrees with the cookie (e.g. cookie expired), the
// next /me call will return 401 and self-heal by clearing the flag.
const SESSION_HINT_KEY = 'cch.session';

function readSessionHint(): boolean {
  try {
    return window.localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    // Private mode / disabled storage — fall through to network probe.
    return true;
  }
}

function writeSessionHint(present: boolean): void {
  try {
    if (present) window.localStorage.setItem(SESSION_HINT_KEY, '1');
    else window.localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // Ignore — storage is best-effort.
  }
}

export default function AuthProvider({ children }: AuthProviderProps) {
  const [customer, setCustomerState] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Wrap setCustomer so every sign-in / sign-out path updates the hint flag
  // automatically — no need to remember to set it at each call site.
  const setCustomer = useCallback((next: Customer | null) => {
    setCustomerState(next);
    writeSessionHint(next !== null);
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const me = await fetchMe();
      // fetchMe returns null only for authoritative 401/404 — route through
      // setCustomer so the hint flag clears and self-heals a stale cookie.
      setCustomer(me);
    } catch {
      // Transient failure (5xx, offline). Clear UI state but leave the hint
      // alone so the next page load still probes /me instead of treating the
      // visitor as anonymous.
      setCustomerState(null);
    } finally {
      setIsLoading(false);
    }
  }, [setCustomer]);

  useEffect(() => {
    // Skip the /me probe for visitors with no prior session. The flag is set
    // when sign-in completes (via setCustomer) and cleared on logout / 401.
    if (!readSessionHint()) {
      setIsLoading(false);
      return;
    }

    // Defer the probe off the initial critical path so it doesn't add to
    // LCP / FCP for cold loads.
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(() => void refresh(), { timeout: 2000 });
      return () => {
        const cancel = (window as unknown as { cancelIdleCallback?: (id: number) => void })
          .cancelIdleCallback;
        if (typeof cancel === 'function') cancel(id);
      };
    }
    const t = window.setTimeout(() => void refresh(), 0);
    return () => window.clearTimeout(t);
  }, [refresh]);

  // Mirror customer.id into the affiliate xcust packer and GA4 user_id so
  // outbound affiliate clicks AND on-site GA4 events share the same identifier
  // — that's what makes the Skimlinks ↔ GA4 join possible.
  useEffect(() => {
    setAffiliateCustomerId(customer?.id ?? null);
    setProductEventsCustomerId(customer?.id ?? null);
    const w = window as Window & { gtag?: (...args: unknown[]) => void };
    if (typeof w.gtag === 'function') {
      w.gtag('set', { user_id: customer?.id ?? null });
    }
  }, [customer]);

  const logout = useCallback(async () => {
    try {
      await apiSignOut();
    } finally {
      setCustomer(null);
    }
  }, [setCustomer]);

  const value = useMemo<AuthContextValue>(
    () => ({
      customer,
      isLoading,
      refresh,
      setCustomer,
      logout,
    }),
    [customer, isLoading, refresh, setCustomer, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
